import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Input,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import {
  EditFilled,
  UserAddOutlined,
  MoreOutlined,
  SearchOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";

import deactivateIcon from "../../assets/svg/user-times-solid.svg";
import EditUser from "./EditUser";
import DeactivateUser from "./DeactivateUser";
import { useUserManagement } from "./hooks/useUserManagement";
import { getUserInfo } from "../../utils/utils";
import { searchTeams } from "../../actionCreator/teamManagementActionCreator";
import { searchRoles } from "../../actionCreator/roleManagementActionCreator";
import { userRoles } from "../../config";
import CustomPagination from "../../common/CustomPagination";
import deleteIcon from "../../assets/images/webToLead/delete.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/webToLead/delete.svg";
import { ReactComponent as ActivateIcon } from "../../assets/svg/icons/activate.svg";
import { getTeamNamesFromUser } from "../../utils/teamAccess";

const { Search } = Input;
const { Option } = Select;

const getAvatarStyles = (firstName = "", lastName = "") => {
  const char1 = firstName?.[0]?.toUpperCase() || "";
  const char2 = lastName?.[0]?.toUpperCase() || "";
  const initials = char1 + char2;

  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors = [
    { bg: "tw-bg-violet-50", text: "tw-text-violet-700", border: "tw-border-violet-100" },
    { bg: "tw-bg-sky-50", text: "tw-text-sky-700", border: "tw-border-sky-100" },
    { bg: "tw-bg-emerald-50", text: "tw-text-emerald-700", border: "tw-border-emerald-100" },
    { bg: "tw-bg-amber-50", text: "tw-text-amber-700", border: "tw-border-amber-100" },
    { bg: "tw-bg-rose-50", text: "tw-text-rose-700", border: "tw-border-rose-100" },
    { bg: "tw-bg-indigo-50", text: "tw-text-indigo-700", border: "tw-border-indigo-100" },
    { bg: "tw-bg-teal-50", text: "tw-text-teal-700", border: "tw-border-teal-100" },
  ];

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const statusOptions = [
  { value: "all", label: "All Users" },
  { value: "true", label: "Active Users" },
  { value: "false", label: "Inactive Users" },
];

const UserListing = () => {
  const dispatch = useDispatch();
  const {
    loading: isLoading,
    handleDelete,
    onFilterChange,
    handleActivateDeactivateUser,
    handleBulkAction,
  } = useUserManagement();
  const { id: userId } = getUserInfo();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const [action, setAction] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [user, setUser] = useState();
  const [teams, setTeams] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleFetching, setRoleFetching] = useState(false);
  const users = useSelector((state) => state.UserManagementReducer.users);
  const activeUserCount = useSelector(
    (state) => state.UserManagementReducer.activeUserCount,
  );
  const loading = useSelector((state) => state.UserManagementReducer.loading);
  const selectedActiveCount = selectedUsers?.filter((u) => u?.active)?.length;
  const selectedInactiveCount = selectedUsers?.filter(
    (u) => !u?.active,
  )?.length;

  const columns = [
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,

      render: (record, row) => {
        const { bg, text, border } = getAvatarStyles(row?.firstName, row?.lastName);
        return (
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className={`tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-size-[40px] tw-border-solid tw-uppercase tw-font-semibold tw-text-sm ${bg} ${text} ${border}`}>
              {row?.firstName?.[0]}
              {row?.lastName?.[0]}
            </div>
            <div className="tw-text-theme tw-font-medium">
              {row?.firstName} {row?.lastName}
            </div>
          </div>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
      render: (record, row) => {
        return (
          <div className="tw-bg-secondary-color tw-rounded-[40px] tw-w-fit tw-py-1 tw-px-6 tw-text-xs tw-font-medium tw-text-text-primary">
            {record}
          </div>
        );
      },
    },
    {
      title: "Team/Hotel",
      dataIndex: "team",
      key: "team",
      sorter: true,
      render: (_, row) => getTeamNamesFromUser(row).join(", "),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (record, row) => {
        return (
          <>
            {record ? (
              <Tag bordered={false} className="active-tag">
                Active
              </Tag>
            ) : (
              <Tag bordered={false} className="deactived-tag">
                Inactive
              </Tag>
            )}
          </>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (record, row) => {
        const items = [
          {
            key: "1",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                className="heading14px"
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("edit");
                  setUser(row);
                }}
              >
                <Col span={6}>
                  <EditFilled />
                </Col>
                <Col span={18}>Edit</Col>
              </Row>
            ),
          },
          {
            key: "2",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                className="heading14px"
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("deactivate");
                  setUser(row);
                }}
              >
                <Col span={6}>
                  {row?.active ? (
                    <img
                      src={deactivateIcon}
                      alt=""
                      style={{ width: 18, height: 18 }}
                    />
                  ) : (
                    <UserAddOutlined />
                  )}
                </Col>

                <Col span={18}>{row?.active ? "Deactivate" : "Activate"}</Col>
              </Row>
            ),
          },
          {
            key: "3",
            label: (
              <Row
                gutter={row?.active ? 12 : 24}
                style={{ color: "#29292B" }}
                onClick={() => {
                  setAction("delete");
                  setUser(row);
                }}
              >
                <Col span={6} className="!tw-pr-0">
                  <img
                    src={deleteIcon}
                    alt=""
                    style={{ width: 18, height: 16 }}
                    className="cursor-pointer"
                  />
                </Col>

                <Col span={18} className="heading14px">
                  Delete
                </Col>
              </Row>
            ),
          },
        ].filter(Boolean);

        return (
          <>
            {userId === row?.id ? (
              <></>
            ) : (
              <Dropdown
                menu={{
                  items,
                }}
                trigger={["click"]}
              >
                <Button type="text" className="tw-more-btn" icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </>
        );
      },
    },
  ];

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (selected) {
        setSelectedUsers([...selectedUsers, record]);
      } else {
        setSelectedUsers(selectedUsers?.filter((d) => d?.id !== record?.id));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      setSelectedUsers(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.role === userRoles.admin, // Column configuration not to be checked
      name: record.role,
    }),
    selectedRowKeys: selectedUsers?.map((d) => d?.id),
  };

  const onSearch = debounce((event) => {
    onFilterChange(
      "globalSearchTerm",
      event?.target?.value ? event?.target?.value : undefined,
    );
    onFilterChange("page", 1);
  }, 500);

  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const res = await dispatch(searchTeams());
        setTeams(res?.content || []);
        const response = await dispatch(searchRoles());
        setRoles(response?.roles || []);
      } catch (error) {}
    };

    fetchDefault();
  }, [dispatch]);

  const fetchOptions = debounce(async (value) => {
    setFetching(true);
    try {
      const res = await dispatch(searchTeams(value));
      setTeams(res?.content || []);
    } catch (err) {}
    setFetching(false);
  }, 500);

  const fetchRoleOptions = debounce(async (value) => {
    setRoleFetching(true);
    try {
      const res = await dispatch(searchRoles(value));
      setRoles(res?.roles || []);
    } catch (err) {}
    setRoleFetching(false);
  }, 500);

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-between gap-10 filters tw-mb-5">
        <Button
          className="tw-btn-premium"
          onClick={() => {
            setUser();
            setAction("add");
          }}
        >
          <UserAddOutlined />
          Create New User
        </Button>
        <div className="tw-flex tw-items-center tw-gap-3">
          {!!selectedUsers?.length && (
            <>
              <Button
                className="!tw-h-[40px] !tw-min-w-[40px] tw-rounded-[10px] tw-border tw-border-solid tw-border-border-secondary !tw-text-sub-text !tw-bg-secondary-color"
                type="text"
                disabled={!selectedInactiveCount}
                onClick={() => {
                  setUser();
                  setAction("activate");
                }}
                icon={<ActivateIcon />}
              />
              <Button
                className="!tw-h-[40px] !tw-min-w-[40px] tw-rounded-[10px] tw-border tw-border-solid tw-border-border-secondary !tw-text-sub-text !tw-bg-secondary-color"
                type="text"
                disabled={!selectedActiveCount}
                onClick={() => {
                  setUser();
                  setAction("deactivate");
                }}
                icon={<MinusCircleOutlined />}
              />
              <Button
                className="!tw-h-[40px] !tw-min-w-[40px] tw-rounded-[10px] tw-border tw-border-solid tw-border-border-secondary !tw-text-sub-text !tw-bg-secondary-color"
                type="text"
                disabled={!selectedUsers?.length}
                onClick={() => {
                  setUser();
                  setAction("delete");
                }}
                icon={<DeleteIcon width={18} height={18} />}
              />
            </>
          )}
          <Input
            defaultValue={searchParams.get("globalSearchTerm")}
            allowClear
            prefix={<SearchOutlined className="tw-text-slate-400" />}
            onChange={onSearch}
            className="tw-input-search-page"
          />
          <Select
            value={searchParams.get("isActive") || "all"}
            optionLabelProp="label"
            options={statusOptions}
            style={{ width: 150 }}
            className="tw-select-search-page"
            onChange={(value) => {
              onFilterChange("isActive", value);
              onFilterChange("page", 1);
            }}
          />
          <Select
            value={searchParams.get("team") || "All Teams"}
            showSearch
            placeholder="Search team..."
            onSearch={fetchOptions}
            filterOption={false}
            className="tw-select-search-page"
            notFoundContent={fetching ? <Spin size="small" /> : null}
            allowClear
            style={{ width: 150 }}
            onChange={(value) => {
              onFilterChange("team", value);
              onFilterChange("page", 1);
            }}
          >
            {teams.map((item) => (
              <Option key={item.id} value={item.teamName}>
                {item.teamName}
              </Option>
            ))}
          </Select>

          <Select
            value={searchParams.get("role") || "All Roles"}
            showSearch
            placeholder="Search role..."
            onSearch={fetchRoleOptions}
            filterOption={false}
            className="tw-select-search-page"
            notFoundContent={roleFetching ? <Spin size="small" /> : null}
            allowClear
            style={{ width: 150 }}
            onChange={(value) => {
              onFilterChange("role", value);
              onFilterChange("page", 1);
            }}
          >
            {roles.map((item) => (
              <Option key={item.id} value={item.name}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="tw-custom-table-container">
        <Table
          rowSelection={{
            type: "checkbox",
            ...rowSelection,
          }}
          scroll={{ x: "max-content" }}
          columns={columns}
          className="tw-custom-table tw-table-max-content"
          dataSource={users?.content?.map((d) => ({ ...d, key: d?.id }))}
          loading={loading}
          showSorterTooltip={true}
          sortDirections={["ascend", "descend"]}
          pagination={false}
          onChange={(pagination, filters, sorter) => {
            onFilterChange(
              "sort",
              !!sorter?.field
                ? sorter?.order === "ascend"
                  ? sorter?.field + ",asc"
                  : sorter?.field + ",desc"
                : undefined,
            );
          }}
        />
        {users?.totalElements > 10 && (
          <CustomPagination
            total={users?.totalElements}
            current={page || 1}
            pageSize={10}
            onChange={(page, pageSize) => {
              onFilterChange("page", page);
            }}
            noBorder={true}
          />
        )}
      </div>

      <EditUser
        user={user}
        isOpen={action === "edit" || action === "add"}
        closeModal={() => {
          setAction();
          setUser();
        }}
      />
      <DeactivateUser
        user={user}
        isLoading={isLoading}
        isOpen={
          action === "deactivate" ||
          action === "activate" ||
          action === "delete"
        }
        isDelete={action === "delete"}
        selectedUsers={
          action === "activate"
            ? selectedUsers?.filter((u) => !u?.active)
            : selectedUsers
        }
        handleCancel={() => {
          setAction();
          setUser();
        }}
        handleAction={() => {
          if (action === "delete") {
            if (user) {
              handleDelete(user);
            } else {
              handleBulkAction({
                action: "DELETE",
                userIds: selectedUsers
                  ?.filter((i) => i?.id !== userId)
                  ?.map((d) => d?.id),
              });
            }
          } else {
            if (user) {
              handleActivateDeactivateUser(user);
            } else {
              if (action === "activate") {
                handleBulkAction({
                  action: "ACTIVATE",
                  userIds: selectedUsers
                    ?.filter((i) => i?.id !== userId)
                    ?.filter((r) => !r?.active)
                    ?.map((d) => d?.id),
                });
              } else {
                handleBulkAction({
                  action: "DEACTIVATE",
                  userIds: selectedUsers
                    ?.filter((i) => i?.id !== userId)
                    ?.filter((r) => r?.active)
                    ?.map((d) => d?.id),
                });
              }
            }
          }
        }}
      />
    </>
  );
};

export default UserListing;
